import { z } from 'zod';

import { Box, Form } from '@ekhodealer/ekho-common/rev';

import type { AiBdcSettings } from '../types';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';

const escalationSchema = z.object({
	email: z
		.string()
		.min(1, 'Escalation email is required')
		.email('Please enter a valid email address'),
});

type EscalationFormData = z.infer<typeof escalationSchema>;

function getDefaultValues(settings: AiBdcSettings | undefined, spEmail: string): EscalationFormData {
	return {
		email: settings?.skills?.escalation?.email ?? spEmail,
	};
}

export function ResponseHandlingSection(): React.JSX.Element {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleSubmit = async (data: EscalationFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				skills: {
					...settings?.skills,
					escalation: {
						enabled: true,
						email: data.email || undefined,
					},
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-escalation'
				schema={escalationSchema}
				defaultValues={getDefaultValues(settings, servicesProvider?.email ?? '')}
				onSubmit={handleSubmit}
				title='Escalation'
				titleLevel='subgroup'
				titleWeight='semibold'
				description="When the AI can't confidently answer a question or a lead becomes frustrated or requests to speak with a person, the conversation is escalated to your team via email at the address below."
				showSaveAndClear>
				<Form.Field.Text
					schemaField='email'
					labelText='Escalation email'
					descriptionText='The email address where escalated conversations will be forwarded.'
					type='email'
					size='large'
					placeholder='support@example.com'
					required
				/>
			</Form>
		</Box>
	);
}
